import { logClientError } from '@utils/server-log';
import { Injectable } from '@angular/core';
import * as AuthProto from '@proto/auth/auth';
import { extractMessageFromUnknown, extractStatusCode } from '@utils/backend-error.utils';
import { CookieService } from 'ngx-cookie-service';
import { endpointBaseGRPC } from 'src/main';
import { BackendLogInterceptor } from '../../interceptors/backend-log.interceptor';

/**
 * Serviço centralizado de gerenciamento de tokens.
 *
 * Responsabilidades:
 * - Armazenamento e leitura de tokens (cookies + localStorage)
 * - Decodificação de JWT para verificar expiração
 * - Refresh de token via gRPC (RefreshToken RPC)
 * - Mutex para evitar múltiplas chamadas de refresh simultâneas
 * - Refresh proativo antes do token expirar (timer + retorno à aba)
 */
@Injectable({
  providedIn: 'root',
})
export class TokenService {
  /** Margem de segurança: renovar 60s antes de expirar */
  private static readonly TOKEN_EXPIRY_BUFFER_SECONDS = 60;

  /** Janela em que um token já validado é reaproveitado sem nova checagem. */
  private static readonly VALIDATION_CACHE_MS = 30_000;

  /** Piso do agendamento proativo: evita loop quente se o token já vier vencido. */
  private static readonly MIN_REFRESH_DELAY_MS = 5_000;

  /** Antecipação aleatória do agendamento, para desalinhar abas concorrentes. */
  private static readonly REFRESH_JITTER_MS = 20_000;

  /** Teto do setTimeout (int32); acima disso o timer dispararia imediatamente. */
  private static readonly MAX_TIMEOUT_MS = 2_147_483_647;

  /**
   * Status que significam "esta credencial não vale mais": a sessão morreu e
   * insistir é inútil. Qualquer outro erro (rede, backend fora do ar) é tratado
   * como transitório, preservando os tokens para uma nova tentativa.
   */
  private static readonly CREDENTIAL_REJECTED_CODES = [3, 5, 7, 16, 401, 403];

  /** Mensagens do Keycloak para refresh token inativo/expirado/revogado. */
  private static readonly CREDENTIAL_REJECTED_PATTERNS = [
    'invalid_grant',
    'token is not active',
    'invalid refresh token',
    'session not active',
  ];

  /** Promise compartilhada para evitar refresh concorrente */
  private refreshInProgress: Promise<boolean> | null = null;

  /** Evita revalidar o mesmo token a cada request gRPC em sequência */
  private lastValidatedAt = 0;

  /**
   * Sessão irrecuperável: o provedor rejeitou o refresh token.
   *
   * Enquanto ligado, nenhuma nova tentativa é feita — repetir só produz
   * `invalid_grant` em série e atrasa o redirect para o login.
   */
  private refreshRejected = false;

  /** Timer do refresh proativo. */
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /** Somente logging: o interceptor de autenticação criaria um ciclo no refresh. */
  private authClient = new AuthProto.auth.v1.AuthServiceClient(
    endpointBaseGRPC + '/auth',
    undefined,
    { unaryInterceptors: [new BackendLogInterceptor()] }
  );

  constructor(
    private cookieService: CookieService
  ) {
    this.watchTabVisibility();
    this.scheduleProactiveRefresh();
  }

  /** Sessão já dada como perdida por rejeição do refresh token. */
  get isRefreshRejected(): boolean {
    return this.refreshRejected;
  }

  // ─── Token Storage ──────────────────────────────────────────

  getAccessToken(): string | null {
    return this.cookieService.get('accessToken') || localStorage.getItem('accessToken') || null;
  }

  getRefreshToken(): string | null {
    return this.cookieService.get('refreshToken') || localStorage.getItem('refreshToken') || null;
  }

  setTokens(accessToken: string, refreshToken: string): void {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 30);

    this.cookieService.set('accessToken', accessToken, { path: '/', expires: expiryDate });
    this.cookieService.set('refreshToken', refreshToken, { path: '/', expires: expiryDate });

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    // Credencial nova: a sessão volta a ser considerada viva.
    this.refreshRejected = false;
    this.lastValidatedAt = Date.now();
    this.scheduleProactiveRefresh();
  }

  clearTokens(): void {
    this.clearScheduledRefresh();

    this.cookieService.delete('accessToken', '/');
    this.cookieService.delete('refreshToken', '/');
    this.cookieService.delete('user', '/');

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');

    sessionStorage.removeItem('oauth_state');
    localStorage.removeItem('oauth_state');
  }

  // ─── JWT Decoding ───────────────────────────────────────────

  /**
   * Verifica se o access token atual está expirado ou prestes a expirar.
   * Retorna true se o token precisa ser renovado.
   */
  isTokenExpiredOrExpiring(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    const payload = this.decodeJwtPayload(token);
    if (!payload || !payload.exp) return true;

    const now = Math.floor(Date.now() / 1000);
    return payload.exp - now <= TokenService.TOKEN_EXPIRY_BUFFER_SECONDS;
  }

  /**
   * Decodifica o payload de um JWT (sem verificar assinatura - apenas leitura).
   */
  private decodeJwtPayload(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  // ─── Token Refresh ──────────────────────────────────────────

  /**
   * Renova o access token usando o refresh token via gRPC.
   * Usa mutex para evitar múltiplas chamadas simultâneas.
   *
   * @param force ignora o token já armazenado e renova de qualquer forma. Use
   * quando o servidor rejeitou explicitamente a credencial (401/16) — só ele
   * sabe que o token local, ainda dentro da validade, não vale mais.
   * @returns true se o refresh foi bem-sucedido, false caso contrário.
   */
  async refreshAccessToken(force = false): Promise<boolean> {
    // Refresh token já rejeitado: não adianta bater de novo no provedor.
    if (this.refreshRejected) {
      return false;
    }

    // Se já há um refresh em andamento, aguarda o resultado
    if (this.refreshInProgress) {
      return this.refreshInProgress;
    }

    this.refreshInProgress = this.doRefresh(force);

    try {
      return await this.refreshInProgress;
    } finally {
      this.refreshInProgress = null;
    }
  }

  private async doRefresh(force: boolean): Promise<boolean> {
    // Cookies e localStorage são compartilhados entre abas: se outra aba já
    // renovou, reaproveitamos. Sem isso, duas abas renovando em paralelo
    // gastariam o mesmo refresh token e uma delas levaria `invalid_grant`
    // quando o provedor rotaciona o token a cada uso.
    if (!force && !this.isTokenExpiredOrExpiring()) {
      this.lastValidatedAt = Date.now();
      return true;
    }

    const currentRefreshToken = this.getRefreshToken();

    if (!currentRefreshToken) {
      logClientError('[TokenService] No refresh token available');
      this.handleRefreshFailure();
      return false;
    }

    try {
      const request = new AuthProto.auth.v1.RefreshTokenRequest({
        refreshToken: currentRefreshToken,
      });

      const response = await this.authClient.RefreshToken(request, {});
      const res = response.toObject();

      if (res.tokens?.accessToken) {
        this.setTokens(
          res.tokens.accessToken,
          res.tokens.refreshToken || currentRefreshToken
        );
        return true;
      }

      logClientError('[TokenService] Refresh response missing tokens');
      this.handleRefreshFailure();
      return false;
    } catch (error) {
      logClientError('[TokenService] Error refreshing token:', error);
      this.handleRefreshFailure(error);
      return false;
    }
  }

  /**
   * Trata a falha do refresh separando os dois casos.
   *
   * Credencial rejeitada: sessão perdida, limpa tudo e marca para não insistir.
   * Falha transitória (rede/backend indisponível): preserva os tokens para que
   * a próxima tentativa reaproveite um refresh token possivelmente válido.
   */
  private handleRefreshFailure(error?: unknown): void {
    if (error !== undefined && !this.isCredentialRejected(error)) {
      logClientError('[TokenService] Falha transitória no refresh; tokens preservados.');
      return;
    }

    this.refreshRejected = true;
    this.clearTokens();
  }

  /** Distingue "credencial inválida" de "não consegui falar com o servidor". */
  private isCredentialRejected(error: unknown): boolean {
    const code = extractStatusCode(error);
    if (code !== null && TokenService.CREDENTIAL_REJECTED_CODES.includes(code)) {
      return true;
    }

    const message = extractMessageFromUnknown(error).toLowerCase();
    return TokenService.CREDENTIAL_REJECTED_PATTERNS.some((pattern) =>
      message.includes(pattern)
    );
  }

  // ─── Proactive Refresh ──────────────────────────────────────

  /**
   * Garante que o token é válido antes de uma request.
   * Se estiver expirado/expirando, faz refresh proativamente.
   *
   * @returns O access token válido, ou null se não há credencial utilizável.
   */
  async ensureValidToken(): Promise<string | null> {
    const currentToken = this.getAccessToken();

    // Sem access token mas com refresh token, a sessão ainda pode estar viva:
    // desistir aqui faria a request sair sem `Authorization`.
    if (!currentToken) {
      if (this.refreshRejected || !this.getRefreshToken()) return null;

      const refreshed = await this.refreshAccessToken();
      return refreshed ? this.getAccessToken() : null;
    }

    const recentlyValidated =
      Date.now() - this.lastValidatedAt < TokenService.VALIDATION_CACHE_MS;

    if (recentlyValidated && !this.isTokenExpiredOrExpiring()) {
      return currentToken;
    }

    if (this.isTokenExpiredOrExpiring()) {
      const refreshed = await this.refreshAccessToken();
      if (!refreshed) return null;
    }

    this.lastValidatedAt = Date.now();
    return this.getAccessToken();
  }

  /**
   * Agenda a renovação para pouco antes do `exp` do access token.
   *
   * Sem isso o refresh só acontece quando alguma request precisa do token: uma
   * aba ociosa deixa o SSO Session Idle do provedor estourar e o refresh token
   * morre ("invalid_grant / Token is not active"), derrubando a sessão mesmo
   * com o navegador aberto.
   */
  private scheduleProactiveRefresh(): void {
    this.clearScheduledRefresh();

    if (this.refreshRejected || !this.getRefreshToken()) return;

    const token = this.getAccessToken();
    const payload = token ? this.decodeJwtPayload(token) : null;
    if (!payload?.exp) return;

    // Jitter: abas abertas no mesmo navegador compartilham o `exp` e
    // disparariam juntas. Antecipar cada uma por um valor aleatório desalinha
    // os pedidos e deixa a primeira renovação servir as demais.
    const jitter = Math.random() * TokenService.REFRESH_JITTER_MS;

    const msUntilExpiry =
      payload.exp * 1000 -
      Date.now() -
      TokenService.TOKEN_EXPIRY_BUFFER_SECONDS * 1000 -
      jitter;

    const delay = Math.min(
      Math.max(msUntilExpiry, TokenService.MIN_REFRESH_DELAY_MS),
      TokenService.MAX_TIMEOUT_MS
    );

    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.refreshAccessToken();
    }, delay);
  }

  private clearScheduledRefresh(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Revalida ao voltar para a aba.
   *
   * Timers de aba oculta são estrangulados pelo navegador (chegam a rodar uma
   * vez por minuto), então o agendamento sozinho não é confiável depois de um
   * período longo em segundo plano.
   */
  private watchTabVisibility(): void {
    if (typeof document === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      if (this.refreshRejected || !this.getRefreshToken()) return;

      if (this.isTokenExpiredOrExpiring()) {
        void this.refreshAccessToken();
      } else {
        this.scheduleProactiveRefresh();
      }
    });
  }
}
